import {selector, selectorFamily} from "recoil";
import {
  getAllUsers,
  getAllOrganizations,
  getBreederById,
  getBreederByQuery,
  getBreedersWithLitters,
  getCertificateById,
  getDogById,
  getDogByOrgRegNumber,
  getDogs,
  getLitterById,
  getLitterBySiblingId,
  getLitters,
  getLittersByBreederId,
  getLittersByParentId,
  getOrganizationById,
  getOrganizationByPrefix,
  getRegisteredDogs,
  getOrganizationLitters,
  getPendingDogs,
  getResultProviderById,
  getResultProviders,
  getTreedogs,
} from "api/publicRoutes";
import {kruValuesState} from "./globalStates";

// ORG BREEDERS FOR ORG PAGE
export const organizationBreedersState = selectorFamily({
  key: "organizationBreedersState",
  get:
    (org) =>
    async ({get}) => {
      const options = {
        sort: {name: 1},
        populate: "organizations.organization",
      };
      const query = {
        organizations: {
          $elemMatch: {
            organization: org._id,
            displayBreeder: true,
          },
        },
      };
      try {
        return await getAllUsers({body: {options, query}});
      } catch (error) {
        if (error.isLoading) {
          throw error.promise;
        } else {
          throw error;
        }
      }
    },
});

// ORG DOGS FOR ORG PAGE
export const registeredDogsState = selectorFamily({
  key: "registeredDogsState",
  get:
    (organizationPrefix) =>
    async ({get}) => {
      const options = {
        sort: {name: 1},
        populate: "organizations.organization",
      };
      try {
        const response = await getRegisteredDogs({organizationPrefix, options});
        return response;
      } catch (error) {
        if (error.isLoading) {
          throw error.promise;
        } else {
          throw error;
        }
      }
    },
});

// NEWEST DOGS FOR ORG PAGE
export const newestRegisteredDogsState = selectorFamily({
  key: "newestRegisteredDogsState",
  get:
    (organizationPrefix) =>
    async ({get}) => {
      const options = {
        limit: 10,
        sort: {createdAt: -1},
        populate: "organizations.organization",
      };
      try {
        const response = await getRegisteredDogs({organizationPrefix, options});
        return response;
      } catch (error) {
        if (error.isLoading) {
          throw error.promise;
        } else {
          throw error;
        }
      }
    },
});

// NEWEST DOGS FOR FRONT PAGE
export const newestDogsState = selector({
  key: "newestDogsState",
  get: async (organizationPrefix) => {
    const options = {limit: 10, sort: {createdAt: -1}};
    const response = await getDogs({organizationPrefix, options});
    if (response.error) {
      throw response;
    }
    return response;
  },
});

// ORG LITTERS FOR ORG PAGE
export const organizationLittersState = selectorFamily({
  key: "organizationLittersState",
  get:
    (org) =>
    async ({get}) => {
      const options = {
        sort: {name: 1},
        populate: "organizations.organization",
      };
      try {
        const response = await getOrganizationLitters({org, options});
        return response;
      } catch (error) {
        if (error.isLoading) {
          throw error.promise;
        } else {
          throw error;
        }
      }
    },
});

// NEWEST LITTERS FOR FRONT PAGE
export const newestLittersState = selector({
  key: "newestLittersState",
  get: async () => {
    const options = {limit: 10, sort: {createdAt: -1}};
    const response = await getLitters({options});
    if (response.error) {
      throw response;
    }
    return response;
  },
});

// NEWEST DOGS FOR ORG PAGE
export const newestOrgLittersState = selectorFamily({
  key: "newestOrgLittersState",
  get:
    (org) =>
    async ({get}) => {
      const options = {
        limit: 10,
        sort: {createdAt: -1},
        populate: "sire dam organizations.organization sire.organizations.organization dam.organizations.organization",
      };
      try {
        return await getOrganizationLitters({org, options});
      } catch (error) {
        if (error.isLoading) {
          throw error.promise;
        } else {
          throw error;
        }
      }
    },
});

// DOG BY ID OR REGNUMBER
export const currentDogState = selectorFamily({
  key: "currentDogState",
  get: (id) => async () => {
    try {
      // Both getDogByOrgRegNumber and getDogById source organizationPrefix themselves, from
      // the current URL (currentOrganizationPrefix() in api/common.js) -- callers never need
      // to pass it in.
      const response = id.length < 12 ? await getDogByOrgRegNumber({regNumber: id}) : await getDogById(id);
      if (response.error) {
        return response;
      }
      return response;
    } catch (e) {
      return { error: true, message: e.message };
    }
  },
});

// DOG BY ORG REGNUMBER
export const currentDogOrgState = selectorFamily({
  key: "currentDogOrgState",
  get:
    ({id, prefix}) =>
    async () => {
      const response = await getDogByOrgRegNumber({
        regNumber: id,
        prefix: prefix,
      });
      if (response?.error) {
        throw response;
      }
      return response;
    },
});

// BREEDER BY ID
export const breederState = selectorFamily({
  key: "breederState",
  get: (id) => async () => {
    const options = {populate: "certificates.certificate"};
    const response = await getBreederById(id, {options});
    if (response.error) {
      throw response;
    }
    return response;
  },
});

// BREEDER BY BREEDERID
export const breederIdState = selectorFamily({
  key: "breederIdState",
  get: (id) => async () => {
    // organizations.organization must be populated -- breederProfile.js's getOrgAndMore()
    // matches on o.organization?._id, which is undefined against a bare (unpopulated) ObjectId.
    // The server no longer autopopulates by default (defaultOptions.js), so every caller that
    // needs a ref expanded has to ask for it explicitly now.
    const options = {populate: "certificates.certificate organizations.organization"};
    const query = {breederId: id};
    const response = await getBreederByQuery({body: {options, query}});
    if (response.error) {
      throw response;
    }
    return response[0];
  },
});

// LITTER BY ID
export const litterByIdState = selectorFamily({
  key: "litterByIdState",
  get: (id) => async () => {
    const response = await getLitterById(id);
    if (response.error) {
      throw response;
    }
    return response;
  },
});

// GET LITTERS BY BREEDER ID
export const littersByBreederIdState = selectorFamily({
  key: "littersByBreederIdState",
  get: (id) => async () => {
    const response = await getLittersByBreederId(id);
    if (response.error) {
      throw response;
    }
    return response;
  },
});

// GET PARENTS LITTERS BY DOG ID
export const littersByParentIdState = selectorFamily({
  key: "littersByParentIdState",
  get:
    ({id, sex}) =>
    async () => {
      const response = await getLittersByParentId(id, sex);
      if (response.error) {
        throw response;
      }
      return response;
    },
});

// GET LITTER BY SIBLINGS ID
export const littersBySiblingIdState = selectorFamily({
  key: "littersBySiblingIdState",
  get: (id) => async () => {
    const response = await getLitterBySiblingId(id);
    if (response.error) {
      throw response;
    }
    return response[0]?.puppies?.filter((puppy) => puppy._id !== id);
  },
});

// GET ALL ORGANIZATIONS
export const organizationsState = selector({
  key: "organizationsState",
  get: async () => {
    const response = await getAllOrganizations();
    if (response.error) {
      throw response;
    }
    return response;
  },
});

// Filter out organizations that have stripe information. Required for registration.
export const stripeOrganizations = selector({
  key: "stripeOrganizationsState",
  get: async ({get}) => {
    const allOrganizations = get(organizationsState);
    return allOrganizations.filter((o) => !!o?.stripe?.account_id);
  },
});

// GET ORGANIZATION BY ID
export const getOrgByIdState = selectorFamily({
  key: "getOrgByIdState",
  get: (id) => async () => {
    const response = await getOrganizationById(id);
    if (response?.error) {
      throw response;
    }
    return response;
  },
});

// GET ORGANIZATION BY PREFIX
export const getOrgByPrefixState = selectorFamily({
  key: "getOrgByPrefixState",
  get: (prefix) => async () => {
    const response = await getOrganizationByPrefix(prefix);
    if (response?.error) {
      throw response;
    }
    return response;
  },
});

// GET CERTIFICATE BY ID
export const getCertByIdState = selectorFamily({
  key: "getCertByIdState",
  get: (id) => async () => {
    const response = await getCertificateById(id);
    if (response.error) {
      throw response;
    }
    return response;
  },
});

// GET RESULTPROVIDER BY ID
export const getResultProviderByIdState = selectorFamily({
  key: "getResultProviderByIdState",
  get: (id) => async () => {
    const response = await getResultProviderById(id);
    if (response.error) {
      throw response;
    }
    return response;
  },
});

// POPULATE CERTIFICATES ARRAY
export const populatedCertsArrayState = selectorFamily({
  key: "populatedCertsArrayState",
  get:
    (certs) =>
    async ({get}) => {
      if (!certs) {
        return "";
      }
      return certs.map((cert) => {
        return {
          ...cert,
          certificate: get(getCertByIdState(cert.certificate)),
        };
      });
    },
});

// POPULATE KRUVALUES ARRAY
export const populatedKruvalueArrayState = selectorFamily({
  key: "populatedKruvalueArrayState",
  get:
    (values) =>
    async ({get}) => {
      const kruValues = get(kruValuesState);
      if (!values) {
        return "";
      }
      return values.map((value) => kruValues.find((v) => v._id === value));
    },
});

// GET ALL DOGS
export const allDogsState = selectorFamily({
  key: "allDogsState",
  get: (organizationPrefix) => async () => {
    const options = {
      limit: 0,
      populate: "organizations.organization",
    };
    const response = await getDogs({ organizationPrefix, options });
    if (response.error) {
      throw response;
    }
    return response;
  },
});

// GET ALL PENDING DOGS
export const allPendingDogsState = selectorFamily({
  key: "allPendingDogsState",
  get: (organizationPrefix) => async () => {
    const options = {
      limit: 0,
      populate: "organizations.organization",
    };
    const response = await getPendingDogs({organizationPrefix, options});
    if (response.error) {
      throw response;
    }
    return response;
  },
});

// GET ALL TREEDOGS
export const allTreedogsState = selectorFamily({
  key: "allTreedogsState",
  get: (organizationPrefix) => async () => {
    const options = {
      limit: 0,
      populate: "organizations.organization certificates.certificate",
    };
    const response = await getTreedogs({ organizationPrefix, options });
    if (response.error) {
      throw response;
    }
    return response;
  },
});

// GET DOG BY ID
export const dogByIdState = selectorFamily({
  key: "dogByIdState",
  get:
    (id) =>
    async ({get}) => {
      const response = await getDogById(id);
      if (response.error) {
        throw response;
      }
      return response;
    },
});

// GET DOG BY DOGOWNER ID
export const dogsByDogOwnerIdState = selectorFamily({
  key: "dogsByDogOwnerIdState",
  get: ({ id, organizationPrefix }) => async ({ get }) => {
    const query = {
      dogOwners: { $in: [id] },
    };
    const response = await getDogs({ organizationPrefix, query });
    if (response.error) {
      throw response;
    }
    return response;
  },
});
// FILTER DOGS BY GENDER
export const dogsBySexState = selectorFamily({
  key: "dogsBySexState",
  get:
    ({ sex, organizationPrefix }) =>
    ({ get }) => {
      const dogs = get(allDogsState(organizationPrefix));
      return dogs.filter((dog) => dog.sex === sex);
    },
});

export const treedogsBySexState = selectorFamily({
  key: "treedogsBySexState",
  get:
    ({ sex, organizationPrefix }) =>
    ({ get }) => {
      const dogs = get(allTreedogsState(organizationPrefix));
      return dogs.filter((dog) => dog.sex === sex);
    },
});

// GET ALL LITTERS
export const allLittersState = selector({
  key: "allLittersState",
  get: async () => {
    const options = {limit: 0};
    const response = await getLitters({options});
    if (response.error) {
      throw response;
    }
    return response;
  },
});

// GET ALL BREEDERS THAT HAVE LITTERS
export const allBreedersState = selector({
  key: "allBreedersState",
  get: async () => {
    const options = {populate: ""};
    const response = await getBreedersWithLitters({options});
    if (response.error) {
      throw response;
    }
    return response;
  },
});

// GET ALL RESULT PROVIDERS
export const allResultProvidersState = selector({
  key: "allResultProvidersState",
  get: async () => {
    const options = {
      limit: 0,
    };
    const response = await getResultProviders({options});
    if (response.error) {
      throw response;
    }
    return response;
  },
});
